/* -------------------------- Design imports start -------------------------- */
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import Cookies from 'js-cookie'
import { createContext, type ReactElement, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from 'react-router-dom'

import { type UnauthWebsocketMessage } from '../features/CADUpload/utils/types'
import LogTool from '../logger/logTools'
import { useUnauthWebsocket } from '../features/CADUpload/hooks/useUnauthWebsocket'
/* ------------------------- Functional imports end ------------------------- */

interface IUnauthWebsocketContext {
  unauthWebsocketMessage: UnauthWebsocketMessage | null
  unauthWebsocketIsReady: boolean
  unauthWebsocketSend: (data: string | ArrayBufferLike | Blob | ArrayBufferView) => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function UnauthWebsocketProvider(props: { children: React.ReactNode }): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'UnauthWebsocketProvider', enable: true, logLevel: 'debug' })
  const location = useLocation()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [isReady, setIsReady] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [message, setMessage] = useState<UnauthWebsocketMessage | null>(null)
  const [accessSecret, setAccessSecret] = useState<string | null>(null)
  /* ----------------------------- Data states end ---------------------------- */

  /* ---------------------------- Route checking start ------------------------- */
  /**
   * Check if the current route should have unauth websocket enabled
   */
  const shouldEnableUnauthWebsocket = (): boolean => {
    const path = location.pathname
    // Routes that should have unauth websocket
    const enabledRoutes = ['/guestRequest', '/embed/cad-upload']
    return enabledRoutes.some(route => path === route || path.startsWith(route))
  }
  /* ---------------------------- Route checking end --------------------------- */

  /* ---------------------------- AccessSecret management start ---------------- */
  /**
   * Get access secret from cookies or URL parameters
   */
  const getAccessSecret = (): string | null => {
    // Try cookie first
    const cookieSecret = Cookies.get('access_secret')
    if (cookieSecret) {
      return cookieSecret
    }

    // Try URL parameter
    const urlParams = new URLSearchParams(window.location.search)
    const urlSecret = urlParams.get('access_secret')
    if (urlSecret) {
      return urlSecret
    }

    return null
  }
  /* ---------------------------- AccessSecret management end ------------------ */

  /* ---------------------------- WebSocket hook setup start ------------------- */
  const {
    isConnected: wsConnected,
    lastMessage: wsLastMessage,
    connect: wsConnect,
    disconnect: wsDisconnect,
    send: wsSend,
  } = useUnauthWebsocket({
    accessSecret: accessSecret,
    onSCSFileReady: () => {
      log.debug('SCS file ready notification received')
    },
    onPropertiesReady: () => {
      log.debug('Properties ready notification received')
    },
    onQuotationLoading: () => {
      log.debug('Quotation loading notification received')
    },
    onQuotationReady: () => {
      log.debug('Quotation ready notification received')
    },
    onError: (error: Error) => {
      log.error('Unauth WebSocket error:', error)
    },
    onFallbackToPolling: () => {
      log.warn('Unauth WebSocket unavailable, falling back to polling')
    },
  })

  // Update message state when websocket receives a message
  useEffect(() => {
    if (wsLastMessage) {
      log.info('Received unauth websocket message:', wsLastMessage)
      setMessage(wsLastMessage)
    }
  }, [wsLastMessage, log])

  // Update ready state when connection status changes
  useEffect(() => {
    setIsReady(wsConnected)
  }, [wsConnected])
  /* ---------------------------- WebSocket hook setup end --------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  /**
   * Effect: Check route and accessSecret, connect/disconnect accordingly
   */
  useEffect(() => {
    const shouldEnable = shouldEnableUnauthWebsocket()
    const secret = getAccessSecret()

    log.debug('Unauth WebSocket effect:', { 
      path: location.pathname, 
      shouldEnable, 
      hasSecret: !!secret 
    })

    if (shouldEnable && secret) {
      // Update access secret if it changed
      if (accessSecret !== secret) {
        log.info('Setting access secret for unauth websocket')
        setAccessSecret(secret)
      }
    } else {
      // Clear access secret if route doesn't need it or no secret available
      if (accessSecret) {
        setAccessSecret(null)
      }
      setIsReady(false)
      setMessage(null)
    }
  }, [location.pathname, accessSecret, log])

  /**
   * Effect: Connect/disconnect when accessSecret changes
   */
  useEffect(() => {
    if (accessSecret && !wsConnected) {
      log.info('Connecting unauth websocket')
      wsConnect()
    } else if (!accessSecret && wsConnected) {
      log.info('Disconnecting unauth websocket (no secret)')
      wsDisconnect()
    }

    // Cleanup on unmount
    return () => {
      if (wsConnected && !accessSecret) {
        log.debug('Cleaning up unauth websocket connection')
        wsDisconnect()
      }
    }
  }, [accessSecret, wsConnected, wsConnect, wsDisconnect, log])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Send message through websocket
   */
  const sendMessage = (data: string | ArrayBufferLike | Blob | ArrayBufferView): void => {
    if (!wsConnected) {
      throw new Error(
        'Unauth websocket is not ready to send messages yet. Wait until unauthWebsocketIsReady is true!'
      )
    }
    wsSend(data)
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const providerValue: IUnauthWebsocketContext = {
    unauthWebsocketMessage: message,
    unauthWebsocketIsReady: isReady,
    unauthWebsocketSend: sendMessage,
  }
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <UnauthWebsocketContext.Provider value={providerValue}>
      {props.children}
    </UnauthWebsocketContext.Provider>
  )
}

const UnauthWebsocketContext = createContext<IUnauthWebsocketContext>({
  unauthWebsocketMessage: null,
  unauthWebsocketIsReady: false,
  unauthWebsocketSend: () => {
    throw new Error('Unauth websocket is not ready to send messages yet. Wait until unauthWebsocketIsReady is true!')
  },
})

export const useUnauthWebsocketContext = (): IUnauthWebsocketContext => useContext(UnauthWebsocketContext)
